import React, { useEffect, useState } from "react";
import {
  Spin,
  Form,
  Row,
  Col,
  Input,
  Button,
  Tooltip,
  InputNumber,
  message,
  DatePicker,
  Modal,
  Select,
} from "antd";
import { useDispatch, useSelector } from "react-redux";
import { SnippetsOutlined } from "@ant-design/icons";
import get from "lodash/get";
import map from "lodash/map";
import indexOf from "lodash/indexOf";
import { updateCompanyTemplatesShiftConfig } from "../../redux/actions/templateActions";
import { SHIFT_SETTING_CONFIG_SUCCESS } from "../../constants/message";
import ProductionCompSetting from "./productionCompSetting";
import {
  checkIfRecordInClipboard,
  initialValuesGenerator,
  shiftPayloadGenerator,
} from "../../utilities/shift";
import SuperVisorInput from "./superVisorInput";
import OperatorInput from "./operatorInput";
import dayjs from "dayjs";

const ShiftSettingForm = ({
  filter,
  initialValues,
  operators,
  superVisors,
  toggle,
  onUpdateConfig,
  config,
  managers,
}) => {
  const [formInitialValues, setFormInitialValues] = useState(initialValues);
  const [loading, setLoading] = useState(false);
  const [clipboardData, setClipboardData] = useState(null);
  const [error, setError] = useState(null);
  const [form] = Form.useForm();
  const { confirm } = Modal;
  const dispatch = useDispatch();
  const user = useSelector((state) => get(state, "auth"));
  const [staffList, setStaffList] = useState({
    operatorList: map(operators, "name"),
    superVisorsList: map(superVisors, "name"),
  });
  const [newStaff, setNewStaff] = useState({
    newOperator: undefined,
    newSupervisor: undefined,
  });

  const onAddOperator = () => {
    if (
      indexOf(get(staffList, "operatorList"), get(newStaff, "newOperator")) ===
      -1
    ) {
      setStaffList({
        ...staffList,
        operatorList: [
          ...get(staffList, "operatorList"),
          get(newStaff, "newOperator"),
        ],
      });
    }

    form.setFieldsValue({
      operator: get(newStaff, "newOperator"),
    });

    setNewStaff({ ...newStaff, newOperator: undefined });
  };

  const onAddSuperVisor = () => {
    if (
      indexOf(
        get(staffList, "superVisorsList"),
        get(newStaff, "newSupervisor"),
      ) === -1
    ) {
      setStaffList({
        ...staffList,
        superVisorsList: [
          ...get(staffList, "superVisorsList"),
          get(newStaff, "newSupervisor"),
        ],
      });
    }

    form.setFieldsValue({
      supervisor: get(newStaff, "newSupervisor"),
    });

    setNewStaff({ ...newStaff, newSupervisor: undefined });
  };

  const onUpdateShiftSetting = (values) => {
    setLoading(true);
    const payload = shiftPayloadGenerator(initialValues, values, filter);
    dispatch(updateCompanyTemplatesShiftConfig(payload))
      .then((res) => {
        setLoading(false);
        message.success(SHIFT_SETTING_CONFIG_SUCCESS);
        onUpdateConfig();
      })
      .catch((err) => {
        setLoading(false);
      });
  };

  const fetchDataFromClipboard = async () => {
    setLoading(true);
    setError(null);
    checkIfRecordInClipboard()
      .then((data) => {
        setClipboardData(data);
      })
      .catch(() => {
        setError("Failed to read clipboard data");
      })
      .finally(() => {
        setLoading(false);
      });
  };

  useEffect(() => {
    fetchDataFromClipboard();
  }, []);

  if (error) {
    return <div style={{ color: "red" }}>Error: {error}</div>;
  }
  const minTime = dayjs(get(initialValues, "start"), "HH:mm:ss");
  const maxTime = dayjs(get(initialValues, "end"), "HH:mm:ss");
  useEffect(() => {
    setFormInitialValues(initialValues);
  }, [initialValues]);
  return (
    <Spin spinning={loading}>
      {get(clipboardData, "company_uuid") === get(filter, "company.id") &&
        get(clipboardData, "templateType") === get(filter, "templateType") && (
          <div className="flex flex-row items-end justify-end">
            <Tooltip title="Paste config">
              <Button
                icon={<SnippetsOutlined />}
                onClick={() => {
                  form.setFieldsValue(
                    initialValuesGenerator(
                      clipboardData,
                      filter,
                      true,
                      dayjs(get(initialValues, "start"), "HH:mm"),
                      dayjs(get(initialValues, "end"), "HH:mm"),
                    ),
                  );
                }}
                size="small"
              >
                Paste
              </Button>
            </Tooltip>
          </div>
        )}
      <Form
        onFinish={(values) => {
          onUpdateShiftSetting(values);
        }}
        form={form}
        layout="vertical"
        requiredMark={false}
        initialValues={initialValuesGenerator(initialValues, filter)}
      >
        <Row gutter={16}>
          <OperatorInput
            newStaff={newStaff}
            staffList={staffList}
            onAddOperator={onAddOperator}
            setNewStaff={setNewStaff}
          />
          <SuperVisorInput
            onAddSuperVisor={onAddSuperVisor}
            setNewStaff={setNewStaff}
            newStaff={newStaff}
            staffList={staffList}
          />
          <Col md={4} xs={24}>
            <Form.Item
              label="Staff Manager"
              name="manager_uuid"
              rules={[
                {
                  required: true,
                  message: "Please select Staff Manager...!",
                },
              ]}
            >
              <Select
                size="small"
                showSearch
                filterOption="label"
                placeholder="Select Manager"
                options={map(managers, (manager) => ({
                  label: get(manager, "name"),
                  value: get(manager, "id"),
                }))}
              />
            </Form.Item>
          </Col>
          <Col md={4} xs={24}>
            <Form.Item
              name="copy_to_date"
              label="Copy to date"
              rules={[
                {
                  required: true,
                  message: "Please select a Copy to date!",
                },
                {
                  validator: (_, value) => {
                    const referenceDate = get(filter, "date");
                    if (!value || !referenceDate) {
                      return Promise.resolve();
                    }
                    const comparisonDate = referenceDate
                      .clone()
                      .subtract(1, "day");

                    if (value.isSameOrBefore(comparisonDate, "day")) {
                      const formattedReferenceDate =
                        referenceDate.format("DD/MM/YYYY");
                      return Promise.reject(
                        new Error(
                          `Copy to date must be after ${formattedReferenceDate}!`,
                        ),
                      );
                    }
                    return Promise.resolve();
                  },
                },
              ]}
            >
              <DatePicker
                format={"DD/MM/YYYY"}
                size="small"
                style={{ width: "100%" }}
              />
            </Form.Item>
          </Col>
          <Col md={4} xs={24}>
            <Form.Item
              label="Wages per hour"
              name="wages_per_hour"
              rules={[
                {
                  required: true,
                  message: "Please input Wages per hour...!",
                },
              ]}
            >
              <InputNumber
                addonBefore="₹"
                size="small"
                addonAfter=""
                defaultValue={0}
                style={{ width: "100%" }}
              />
            </Form.Item>
          </Col>
          <Col xs={24}>
            <ProductionCompSetting
              minTime={minTime}
              maxTime={maxTime}
              form={form}
              filter={filter}
              initialValues={initialValuesGenerator(formInitialValues, filter)}
              config={config}
            />
          </Col>
        </Row>
      </Form>

      <div className="w-full bg-white p-4">
        <div className="flex justify-end space-x-2">
          <Button
            onClick={() => {
              toggle();
            }}
            className="border-gray-300"
          >
            Close
          </Button>
          <Button
            type="primary"
            htmlType="submit"
            onClick={() => {
              if (
                form.getFieldValue("copy_to_date") &&
                get(filter, "date") &&
                form
                  .getFieldValue("copy_to_date")
                  .isAfter(get(filter, "date"), "day")
              ) {
                confirm({
                  maskClosable: true,
                  title: "Copy shift config!",
                  content: `Are you sure you want to copy this shift config till date ${form.getFieldValue("copy_to_date").format("DD/MM/YYYY")}?`,
                  okText: "Yes",
                  cancelText: "No",
                  okType: "primary",
                  onOk() {
                    form.submit();
                  },
                });
              } else {
                form.submit();
              }
            }}
          >
            Submit
          </Button>
        </div>
      </div>
    </Spin>
  );
};

export default ShiftSettingForm;
